import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { PoundSterling, ChevronRight } from 'lucide-react';
import {
  TripTypeSelector,
  PaidLeaveFields,
  WageAdjustmentFields,
} from './form';
import DateInput from './form/DateInput';

/**
 * Step 1 of the Trip Wizard:
 * - Contract selection (for contract-linkable types)
 * - Trip type selector
 * - Info banners per type
 * - Inline forms for wage adjustment, paid leave, qualifying holiday
 */
const TripWizardStep1 = ({
  formData, setFormData,
  contracts, applyContract, lastTripContract,
  contractDialogOpen, setContractDialogOpen,
  goToNextStep,
  isContractLinkable, isWorkTrip, isPaidLeave, isWageAdjustment,
  isUkHotelStay, isCourseUk, isHotelStay,
  dateContext,
}) => {
  return (
    <>
      {/* Contract Selection UI — shown first, before Trip Type */}
      {isContractLinkable && (
        <div className="space-y-3">
          <Label className="text-slate-300 text-sm font-medium">Contract</Label>

          {contracts.length === 0 ? (
            <div className="p-4 bg-slate-700/30 rounded-lg border border-dashed border-slate-600 text-center space-y-3">
              <p className="text-sm text-slate-400">No contracts found. Add your contract details to auto-fill pay rates.</p>
              <Button
                type="button"
                onClick={() => setContractDialogOpen(true)}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="add-first-contract-btn"
              >
                <PoundSterling className="h-4 w-4 mr-2" />
                Add Contract Details
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Same as last trip shortcut */}
              {lastTripContract && !formData.contract_id && (
                <button
                  type="button"
                  onClick={() => {
                    applyContract(lastTripContract);
                    if (isWorkTrip) goToNextStep();
                  }}
                  className="w-full p-3 bg-amber-600/10 hover:bg-amber-600/20 rounded-lg border border-amber-500/30 text-left transition-all group"
                  data-testid="same-contract-btn"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-amber-400">Same contract as last trip</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {lastTripContract.employer_name}{lastTripContract.client_name ? ` / ${lastTripContract.client_name}` : ''} — {lastTripContract.contract_type === 'salaried' ? 'Salaried' : `£${lastTripContract.daily_rate}/day`}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              )}

              {/* Contract dropdown */}
              <select
                value={formData.contract_id}
                onChange={(e) => {
                  const cid = e.target.value;
                  if (!cid) {
                    setFormData(prev => ({ ...prev, contract_id: '' }));
                    return;
                  }
                  const selectedContract = contracts.find(c => c.id === cid);
                  if (selectedContract) applyContract(selectedContract);
                }}
                className="w-full bg-slate-900 border border-slate-600 text-white rounded-md px-3 py-2 text-sm"
                data-testid="contract-selector"
              >
                <option value="">Select a contract...</option>
                {contracts.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.employer_name}{c.client_name ? ` / ${c.client_name}` : ''} — {c.contract_type === 'salaried' ? 'Salaried' : `£${c.daily_rate}/day`}
                  </option>
                ))}
              </select>

              {/* Selected contract summary */}
              {formData.contract_id && (() => {
                const c = contracts.find(ct => ct.id === formData.contract_id);
                if (!c) return null;
                return (
                  <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600/30 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400 font-medium">Selected Contract</span>
                      <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">
                        {c.contract_type === 'salaried' ? 'Salaried' : 'Day Rate'}
                      </Badge>
                    </div>
                    {c.contract_type === 'salaried' ? (
                      <p className="text-sm text-white">
                        £{c.monthly_base?.toLocaleString('en-GB')}/month + £{c.daily_uplift}/day uplift
                      </p>
                    ) : (
                      <p className="text-sm text-white">£{c.daily_rate?.toLocaleString('en-GB')}/day</p>
                    )}
                    {(c.travel_day_rate || c.hotel_day_rate) && (
                      <p className="text-xs text-slate-400">
                        {c.travel_day_rate ? `Travel: £${c.travel_day_rate}/day` : ''}
                        {c.travel_day_rate && c.hotel_day_rate ? ' · ' : ''}
                        {c.hotel_day_rate ? `Hotel: £${c.hotel_day_rate}/day` : ''}
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* New Contract button */}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setContractDialogOpen(true)}
                className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-7 text-xs px-2"
                data-testid="add-contract-from-trip"
              >
                <PoundSterling className="h-3 w-3 mr-1" />
                New Contract
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Trip Type Selector */}
      <TripTypeSelector
        value={formData.trip_type}
        onChange={(value) => {
          setFormData({ ...formData, trip_type: value });
        }}
      />

      {/* Info banners */}
      {(isUkHotelStay || isCourseUk) && (
        <div className="p-3 bg-slate-700/50 rounded-lg border border-slate-600">
          <p className="text-sm text-slate-400">
            <span className="font-medium text-slate-300">Note:</span> UK-based {isCourseUk ? 'courses' : 'hotel stays'} do not count towards qualifying days for SED, but you can still record any earnings received during this period.
          </p>
        </div>
      )}

      {isHotelStay && (
        <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
          <p className="text-sm text-slate-400">
            <span className="font-medium text-amber-400">Foreign Hotel Stay:</span> Record standby pay or day rates while staying in a hotel abroad. These days count towards your qualifying days.
          </p>
        </div>
      )}

      {isPaidLeave && (
        <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
          <p className="text-sm text-slate-400">
            <span className="font-medium text-purple-400">Paid Leave:</span> Record holiday pay or other paid leave. Enter the dates and your day rate - earnings will be calculated automatically.
          </p>
        </div>
      )}

      {isWageAdjustment && (
        <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
          <p className="text-sm text-slate-400">
            <span className="font-medium text-amber-400">Wage Adjustment:</span> Record bonus payments, deductions, or other miscellaneous additions/subtractions to your wages.
          </p>
        </div>
      )}

      {/* Non-contract Step 1 fields */}
      {isPaidLeave && (
        <PaidLeaveFields formData={formData} setFormData={setFormData} />
      )}

      {isWageAdjustment && (
        <>
          <WageAdjustmentFields formData={formData} setFormData={setFormData} />
          <div className="space-y-2">
            <Label className="text-slate-300">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..."
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              rows={3}
            />
          </div>
        </>
      )}

      {formData.trip_type === 'qualifying_holiday' && (
        <>
          <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <p className="text-sm text-slate-400">
              <span className="font-medium text-amber-400">Qualifying Holiday:</span> Record time spent abroad on holiday that counts towards your qualifying days for SED.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Destination(s) *</Label>
            <Input
              value={formData.foreign_ports}
              onChange={(e) => setFormData({ ...formData, foreign_ports: e.target.value })}
              placeholder="e.g., Spain, Portugal (comma separated)"
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              data-testid="holiday-destination-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Date Left UK *</Label>
              <DateInput contextDate={dateContext}
                value={formData.date_left_uk}
                onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="holiday-date-left-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Date Returned UK *</Label>
              <DateInput contextDate={dateContext}
                value={formData.date_returned_uk}
                onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
                data-testid="holiday-date-returned-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes about the holiday..."
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              rows={2}
            />
          </div>
        </>
      )}
    </>
  );
};

export default TripWizardStep1;
